/**
 * Фото в редактировании профиля (U-03, изменение v1.15 — вместо экрана U-04).
 *
 * Сверху — как «Set New Photo» в редактировании Telegram: круглый аватар и
 * «Выбрать фото». Под ним лента снимков в кадре 4:5: первый — главный, нажатие
 * открывает просмотр с действиями (сделать главным, порядок, удалить),
 * последняя плитка добавляет ещё одно. Пока фото загружается, на аватаре и в
 * ленте видно его превью с прогрессом, затем «обработку».
 *
 * U-09: снятое модерацией фото подписано причиной — видит только владелец.
 */
import { PHOTO_REJECTED_LABEL, photoRejectionText } from '@entities/user/photo-state'
import { Avatar, Icon, Spinner, cx } from '@shared/ui'

import type { ProfilePhotos } from './use-profile-photos'
import styles from './photos.module.css'

function thumbUrl(photo: ProfilePhotos['photos'][number]): string | undefined {
  return photo.variants.find((v) => v.name === 'medium')?.url ?? photo.variants[0]?.url
}

/** Кольцо прогресса поверх превью: передача байтов, затем обработка на сервере. */
function UploadProgress({ stage, progress }: { stage: string; progress: number }): React.ReactElement {
  const uploading = stage === 'uploading' || stage === 'preparing'
  const percent = Math.round(progress * 100)
  // Окружность r = 22: длина ≈ 138
  const length = 2 * Math.PI * 22

  return (
    <span className={styles['progress']} aria-live="polite">
      {uploading ? (
        <svg viewBox="0 0 48 48" className={styles['progressRing']} aria-label={`Загружено ${percent}%`}>
          <circle cx="24" cy="24" r="22" className={styles['progressTrack']} />
          <circle
            cx="24"
            cy="24"
            r="22"
            className={styles['progressValue']}
            strokeDasharray={length}
            strokeDashoffset={length * (1 - progress)}
          />
        </svg>
      ) : (
        <Spinner size={22} />
      )}
      <span className="visually-hidden">{uploading ? `Загрузка ${percent}%` : 'Обработка фото'}</span>
    </span>
  )
}

export function PhotoEditor({
  name,
  model,
}: {
  name: string | null
  model: ProfilePhotos
}): React.ReactElement {
  const { photos, upload, canAdd, max } = model
  const uploading = upload.stage !== 'idle' && upload.stage !== 'failed' && upload.stage !== 'rejected'

  return (
    <section className={styles['editor']} aria-label="Фотографии профиля">
      <div className={styles['avatarBlock']}>
        <button
          type="button"
          className={styles['avatarButton']}
          aria-label={photos.length ? 'Открыть фото профиля' : 'Выбрать фото'}
          onClick={() => (photos.length ? model.openViewer(0, true) : model.choose('main'))}
        >
          {uploading && upload.previewUrl ? (
            <img className={styles['avatarPreview']} src={upload.previewUrl} alt="" />
          ) : (
            <Avatar name={name} photo={photos[0] ?? null} size="2xl" />
          )}
          {uploading ? <UploadProgress stage={upload.stage} progress={upload.progress} /> : null}
          {!photos.length && !uploading ? (
            <span className={styles['avatarCamera']} aria-hidden>
              <Icon name="camera" size={26} />
            </span>
          ) : null}
        </button>

        <button
          type="button"
          className={styles['avatarAction']}
          disabled={uploading}
          onClick={() => model.choose('main')}
        >
          {photos.length ? 'Изменить фото' : 'Выбрать фото'}
        </button>
      </div>

      {photos.length || uploading ? (
        <div className={styles['strip']} role="list" aria-label="Все фото">
          {photos.map((photo, index) => {
            const url = thumbUrl(photo)
            const reason = photoRejectionText(photo)
            return (
              <button
                key={photo.id}
                type="button"
                role="listitem"
                className={cx(styles['thumb'], reason && styles['thumbRejected'])}
                aria-label={`Фото ${index + 1}${index === 0 ? ', главное' : ''}${reason ? `, ${PHOTO_REJECTED_LABEL}` : ''}`}
                onClick={() => model.openViewer(index, true)}
              >
                {url ? <img src={url} alt="" className={styles['thumbImage']} /> : null}
                {index === 0 ? <span className={styles['mainBadge']}>Главное</span> : null}
                {reason ? (
                  <span className={styles['thumbState']} title={reason}>
                    {PHOTO_REJECTED_LABEL}
                  </span>
                ) : null}
              </button>
            )
          })}

          {uploading ? (
            <span className={cx(styles['thumb'], styles['thumbUploading'])} role="listitem">
              {upload.previewUrl ? (
                <img src={upload.previewUrl} alt="" className={styles['thumbImage']} />
              ) : null}
              <UploadProgress stage={upload.stage} progress={upload.progress} />
              <button type="button" className={styles['cancelUpload']} onClick={model.cancelUpload}>
                Отменить
              </button>
            </span>
          ) : canAdd ? (
            <button
              type="button"
              role="listitem"
              className={cx(styles['thumb'], styles['addThumb'])}
              onClick={() => model.choose('add')}
            >
              <Icon name="plus" size={24} />
              <span>Добавить</span>
            </button>
          ) : null}
        </div>
      ) : null}

      <p className={styles['footnote']}>
        {photos.length
          ? `Первое фото — главное: оно на аватаре и в подборке «Движа». Нажмите на фото, чтобы сделать его главным, переставить или удалить. ${photos.length} из ${max}.`
          : 'Без фото в «Движе» вас не показывают. Фото видно сразу после загрузки.'}
      </p>
    </section>
  )
}
